import {getTestProps} from '@benchling/js-test-utils';
import {Badge, MantineColor} from '@mantine/core';
import React from 'react';

import {ProcessTaskStatusType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';

import {CardSectionRowProps} from '../CardSection';
import {DEFAULT_BADGE_PROPS, HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';
import {getHomepageFileOutlineIcon} from '../HomepageIconUtil';
import {trackHomepageEvent} from '../HomepageUtils';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

function getStatusBadgeLabel(status: ReviewStatus | undefined): string {
  if (status === ReviewStatus.RETRACTED) {
    return 'Retracted';
  }
  return ReviewUtil.getStatusTextFromReview(status) ?? 'In progress';
}

const STATUS_BADGE_COLORS: Partial<Record<ReviewStatus | ProcessTaskStatusType, MantineColor>> = {
  [ReviewStatus.ACCEPTED]: 'green',
  [ReviewStatus.REJECTED]: 'red',
  [ReviewStatus.NEEDS_REVIEW]: 'yellow',
  [ReviewStatus.RETRACTED]: 'gray',
  [ProcessTaskStatusType.PENDING]: 'gray',
  [ProcessTaskStatusType.IN_PROGRESS]: 'blue',
  [ProcessTaskStatusType.COMPLETED]: 'green',
  [ProcessTaskStatusType.CANCELLED]: 'red',
  [ProcessTaskStatusType.FAILED]: 'red',
  [ProcessTaskStatusType.INVALID]: 'orange',
  [ProcessTaskStatusType.SKIPPED]: 'gray',
};

function getStatusBadgeColor(status: ReviewStatus | ProcessTaskStatusType | undefined): MantineColor {
  if (status == null) {
    return 'blue';
  }
  return STATUS_BADGE_COLORS[status] ?? 'blue';
}

interface ReviewableListItem {
  id: string;
  name: string;
  modifiedAt: string;
  editUrl: string;
  review?: {status: ReviewStatus} | null;
}

export function reviewableRowProps(item: ReviewableListItem): CardSectionRowProps {
  return {
    id: item.id,
    icon: getHomepageFileOutlineIcon({id: item.id, tagSchema: null, type: null}),
    name: item.name,
    modifiedAt: item.modifiedAt,
    href: item.editUrl,
    onClick: () => {
      trackHomepageEvent('my_work_reviewable_clicked');
      SidepanelActions.close({autoOpenFile: false});
    },
    rightContent: (
      <Badge
        color={getStatusBadgeColor(item.review?.status)}
        {...DEFAULT_BADGE_PROPS}
        {...getTestProps({
          component: TEST_COMPONENT,
          element: 'statusBadge',
          key: item.review?.status ?? 'none',
        })}
      >
        {getStatusBadgeLabel(item.review?.status)}
      </Badge>
    ),
  };
}

interface ProcessTaskStatus {
  statusType: ProcessTaskStatusType;
  displayName: string;
}

interface TaskListItem {
  id: string;
  name: string;
  modifiedAt: string;
  url: string;
  status: ProcessTaskStatus | null;
}

export function taskRowProps({id, name, modifiedAt, url, status}: TaskListItem): CardSectionRowProps {
  return {
    id,
    icon: getHomepageFileOutlineIcon({id, tagSchema: null, type: null}),
    name,
    modifiedAt,
    href: url,
    onClick: () => {
      trackHomepageEvent('my_work_task_clicked');
      SidepanelActions.close({autoOpenFile: false});
    },
    rightContent: (
      <Badge
        color={getStatusBadgeColor(status?.statusType)}
        {...DEFAULT_BADGE_PROPS}
        {...getTestProps({
          component: TEST_COMPONENT,
          element: 'statusBadge',
          key: status?.statusType ?? 'none',
        })}
      >
        {status?.displayName}
      </Badge>
    ),
  };
}
